/**
 * Easter tee designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterTeeDesignsPack: ToilePack = {
    slug: 'easter-tee-designs',
    category: 'season',
    name: 'Easter tee designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Slogan', subject: 'The words "HOPPY EASTER" lettered in bold retro type with bunny ears, isolated on a plain ground'},
        {key: 'bunny', label: 'Cartoon bunny', subject: 'A cartoon bunny face with a bow tie, isolated on a plain ground, no text'},
        {key: 'badge', label: 'Retro badge', subject: 'A painted egg in a retro sunburst circle with a distressed texture, isolated, no text'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['in a smaller kids size', 'with a bowtie added', 'as a single-color print'],
    directionHint: 'try: in a smaller kids size',
    seo: {
        title: 'Easter Tee Designs: Slogan, Bunny & Badge',
        metaDescription: 'Nine Easter t-shirt designs — Hoppy Easter slogan, cartoon bunny, retro egg badge — in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter t-shirt design: slogan, cartoon bunny, retro badge',
        tagline: 'Nine designs, three graphics, three treatments each.',
        intro: 'Nine Easter designs for the chest, isolated on a plain ground with no shirt shown: a Hoppy Easter slogan with bunny ears, a cartoon bunny face with a bow tie, and a retro egg sunburst badge, each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Ready to print on any garment.',
        useCases: [
            'a print-on-demand t-shirt listing',
            'a family Easter matching shirt',
            'a heat-transfer design for a home printer',
            'a tote bag or apron graphic',
        ],
        faq: [
            {q: 'Is the shirt included, or just the design?', a: 'Just the design — each file is the artwork alone on a plain background, ready to place onto any shirt, tote, or apron in a mockup tool or print service.'},
            {q: 'Can I use this for print-on-demand?', a: 'Yes — the designs are cleared for commercial use, including print-on-demand storefronts.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Does the slogan design come with real, legible text?', a: "Yes — 'Hoppy Easter' is lettered directly into the design, spelled out clean and legible, not placeholder text."},
        ],
    },
}
